import { useState } from "react";

import { Classes, Colors, Icon } from "@blueprintjs/core";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

import { Box, Flex } from "@/components/layout/flexbox";
import { CancelMeetingConfirmDialog } from "@/components/pieces/coaching/meetings/CancelMeetingConfirmDialog";
import { AttendingDropdownButton } from "@/components/pieces/coaching/meetings/MeetingAttendees/components/AttendingButton";
import { MeetingHeaderButton } from "@/components/pieces/coaching/meetings/MeetingHeaderButton";
import { getUser } from "@/components/session/JwtTokenManager";
import { STANDARD_APP_MAX_WIDTH, SUBTEXT_COLOR } from "@/css/constants";
import { FullMeeting } from "@/types/meetings";
import { RoleView } from "@/types/users";
import { formatDate } from "@/utils/datetime";

interface MeetingHeaderViewProps {
  productName: string;
  loading: boolean;
  meetingDate: string;
  attendingButton?: React.ReactNode;
  menuButton?: React.ReactNode;
  canceled: boolean;
  isCoach: boolean;
  customerId?: string;
  view: RoleView;
}

function MeetingHeaderView({
  productName,
  loading,
  meetingDate,
  attendingButton,
  menuButton,
  canceled,
  isCoach,
  customerId,
}: MeetingHeaderViewProps) {
  return (
    <Box className="bbs" py={[2, 24]} bg={Colors.WHITE}>
      <Box maxWidth={STANDARD_APP_MAX_WIDTH} m="0 auto" px={[3, 24]}>
        <Box mb={3}>
          {isCoach ? (
            <Link to={`/client/${customerId}/sessions`}>
              <Icon icon="chevron-left" size={18} /> Back To Client
            </Link>
          ) : (
            <Link to="/sessions">
              <Icon icon="chevron-left" size={18} /> View All Sessions
            </Link>
          )}
        </Box>
        <Flex justifyContent="space-between" alignItems="center">
          <Box>
            <Box
              fontSize={28}
              fontWeight={600}
              className={loading ? Classes.SKELETON : undefined}
            >
              {productName}
            </Box>
            <Flex
              fontSize={18}
              color={SUBTEXT_COLOR}
              mt={1}
              alignItems="center"
              className={loading ? Classes.SKELETON : undefined}
              style={{
                textDecoration: canceled ? "line-through" : undefined,
              }}
            >
              <Icon icon="calendar" /> <Box ml={1}>{meetingDate}</Box>
            </Flex>
          </Box>

          {
            /** Note:
             * Currently attendingButton just for attendee
             * while menuButton is not for attendee aswell
             * */
            attendingButton ? attendingButton : menuButton
          }
        </Flex>
      </Box>
    </Box>
  );
}

interface MeetingHeaderProps {
  meeting?: FullMeeting;
  loading?: boolean;
  view?: RoleView;
  customerId?: string;
}

export function MeetingHeader({
  meeting,
  loading,
  view = "coach",
  customerId = "",
}: MeetingHeaderProps) {
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
  const currentUser = getUser();
  const navigate = useNavigate();

  if (loading) {
    return (
      <MeetingHeaderView
        loading={true}
        productName="Loading Data"
        canceled={false}
        meetingDate="Loading the meeting date"
        isCoach={false}
        view={view}
      />
    );
  }

  const isCoach = currentUser?.id === meeting?.coach?.id;

  let meetingDate = meeting?.eventAt
    ? formatDate(meeting.eventAt, "eeee, MMM d 'at' h:mm a z")
    : "Awaiting Scheduling";

  if (meeting?.canceledAt) meetingDate = "Canceled";

  const currentCustomerId = customerId || meeting?.customer?.id;

  return (
    <>
      <MeetingHeaderView
        loading={false}
        productName={meeting?.product?.name || ""}
        attendingButton={
          meeting?.allowInvitation && meeting?.attendee ? (
            <AttendingDropdownButton attendee={meeting?.attendee} />
          ) : null
        }
        menuButton={
          meeting && (
            <MeetingHeaderButton
              meeting={meeting}
              isCoach={isCoach}
              setSentSuccessVisible={() => toast.success("Invite Sent")}
              onCancel={setShowCancelConfirm}
              label={
                <div className="size-6">
                  <Icon icon="more" color={SUBTEXT_COLOR} size={24} />
                </div>
              }
              view={view}
            />
          )
        }
        meetingDate={meetingDate || "Awaiting Scheduling"}
        canceled={!!meeting?.canceledAt}
        isCoach={isCoach}
        customerId={currentCustomerId}
        view={view}
      />
      {meeting && (
        <CancelMeetingConfirmDialog
          meeting={meeting}
          isCoach={isCoach}
          isOpen={showCancelConfirm}
          onCancel={() => setShowCancelConfirm(false)}
          onConfirm={() => navigate("/sessions")}
          customerId={currentCustomerId}
        />
      )}
    </>
  );
}
